import styles from './SingleAssetDetails.module.scss';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import InformationSection from '@customer/routes/asset/components/info-section/InformationSection';
import SingleAssetSubPage, {
  HeaderItem,
} from '@customer/templates/single-asset-sub-page/SingleAssetSubPage';
import { AccordionStatus } from '@customer/components/accordions/types';
import {
  AssetComponentModel,
  SystemTypePpu,
  useGetSingleAssetsQuery,
} from '@customer/store/apis/assetsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ReportingItem,
  useAssetReportingTableData,
} from '@customer/hooks/assets/useAssetReportingTableData';
import {
  columnDefBootloaderVersion,
  columnDefComponentId,
  columnDefComponentType,
  columnDefFirmwareVersion,
  columnDefHardwareRevision,
  columnDefSerialNumber,
  columnDefTechLibraryFilters,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import MapView from '@customer/components/maps/map-view/MapView';
import SingleAssetPositionLayer from '@customer/components/maps/single-asset-pos/SingleAssetPositionLayer';
import { useAssetPositionsOnMap } from '@customer/components/maps/hooks/useAssetPositionsOnMap';
import { getObjectType } from '@cas/utils/translationHelpers';
import AllGeofencesLayer from '@customer/components/maps/all-geofences-layer/AllGeofencesLayer';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import { useShowLibrary } from '@customer/hooks/assets/useShowLibrary';
import { useReportingColDefs } from './useReportingColDefs';

const GridSliceKeyReporting = 'AssetReporting';
const GridSliceKeyComponents = 'AssetComponents';

const getRowIdReporting = (data: ReportingItem) => data.evt;
const getRowIdComponents = (data: AssetComponentModel) => data.type;

const reportRowClassRules: RowClassRules<ReportingItem> = {
  'dm-bold-row': (params: RowClassParams<ReportingItem>) => Boolean(params.data?.error),
};

export function SingleAssetDetails() {
  const { assetId } = useParams();
  const { t } = useTranslation();

  const { currentData: asset } = useGetSingleAssetsQuery(assetId ?? skipToken, {
    refetchOnMountOrArgChange: 10,
  });

  const headerItems = useMemo(() => {
    const items: HeaderItem[] = [
      { titleTransId: 'DeviceID', value: asset?.did },
      { titleTransId: 'ObjectType', value: getObjectType(t, asset?.obj) },
    ];

    if (asset?.hw !== SystemTypePpu) {
      items.push({ titleTransId: 'Description', value: asset?.desc });
      items.push({ titleTransId: 'CasSoftware', value: asset?.casSw });
      items.push({ titleTransId: 'CasOS', value: asset?.osVer });
    }

    return items;
  }, [asset?.casSw, asset?.desc, asset?.did, asset?.hw, asset?.obj, asset?.osVer, t]);

  const { good: reportingGood, items: reportingItems } = useAssetReportingTableData({ asset });

  const columnDefsReporting = useReportingColDefs();

  const columnDefsComponents: ColDef<AssetComponentModel>[] = useMemo(
    () => [
      { ...columnDefComponentType(t, asset?.hw) },
      { ...columnDefFirmwareVersion(t) },
      { ...columnDefBootloaderVersion(t) },
      { ...columnDefSerialNumber(t) },
      { ...columnDefHardwareRevision(t) },
      { ...columnDefComponentId(t) },
      { ...columnDefTechLibraryFilters(t) },
    ],
    [asset?.hw, t],
  );

  const mapProps = useAssetPositionsOnMap(asset);

  const onShowLibrary = useShowLibrary(asset?.docFlt);

  const relatedDocs = useMemo(() => {
    if (asset?.docFlt) {
      return (
        <div className={styles.actions}>
          <VdsButton variant="ghost" onClick={onShowLibrary}>
            {t('RelatedDocuments')}
            <VdsIcon slot="icon-right" svgIcon="open-icon" />
          </VdsButton>
        </div>
      );
    }

    return null;
  }, [asset?.docFlt, onShowLibrary, t]);

  return (
    <SingleAssetSubPage headerItems={headerItems} headerActions={relatedDocs}>
      <InformationSection
        status={reportingGood ? AccordionStatus.Healthy : AccordionStatus.Danger}
        icon={reportingGood ? 'good' : 'information'}
        title={t('ReportingStatus')}
      >
        <GridControlled<ReportingItem>
          sliceKey={GridSliceKeyReporting}
          rowData={reportingItems}
          columnDefs={columnDefsReporting}
          getRowId={getRowIdReporting}
          rowClassRules={reportRowClassRules}
          noRowsMessage={t('No data')}
          autoheight
        />
      </InformationSection>

      {asset?.hw !== SystemTypePpu && (
        <InformationSection
          status={AccordionStatus.Informative}
          icon="asset"
          title={t('Components')}
        >
          <GridControlled<AssetComponentModel>
            sliceKey={GridSliceKeyComponents}
            rowData={asset?.components}
            columnDefs={columnDefsComponents}
            getRowId={getRowIdComponents}
            noRowsMessage={t('No components available')}
            autoheight
          />
        </InformationSection>
      )}

      {asset && (asset.posG || asset.posS) && (
        <InformationSection
          status={AccordionStatus.Informative}
          icon="map"
          title={t('AssetLocation')}
          isPadded={false}
        >
          <div className={styles.map}>
            <MapView {...mapProps} showLayersControl={true} padding={true}>
              <AllGeofencesLayer checked={false} />
              <SingleAssetPositionLayer asset={asset} />
            </MapView>
          </div>
        </InformationSection>
      )}
    </SingleAssetSubPage>
  );
}

export default SingleAssetDetails;
